import React, { useContext, useState, useEffect, useMemo } from "react";
import set from "lodash/set";
import useAsyncRetry from "react-use/lib/useAsyncRetry";
import { LocalBusiness } from "../../types";
import debug from "../../utils/debug";
import { get, isFunction } from "../../utils/helpers";
import { getNameByLocale } from "../../utils/language";
import { loadStores, getStoreById } from "../../utils/stores";
import {
  AppState,
  APP_PAGE,
  checkAppCorrectlySetup,
  actions
} from "../../hooks/useAppState";
import useT, { TFunction } from "../../hooks/useT";
import AppFrame from "../../components/AppFrame";
import Page from "../../components/Page";
import Button from "../../components/Button";
import {
  ReactSelectOptionObject,
  ReactSelectChangeEventHandler
} from "../../components/InputSelectCustom";
import Field from "../../components/Field";
import AppLayoutHeaderLeftCancel from "../AppLayout/AppLayoutHeaderLeftCancel";
import { AppStateContext } from "../App";

interface SetupFormState {
  language: string;
  zone: string;
  storeId: string;
  storeCountry: string;
}

interface SetupFormErrors {
  response: React.ReactNodeArray;
  language: React.ReactNodeArray;
  zone: React.ReactNodeArray;
  storeId: React.ReactNodeArray;
  storeCountry: React.ReactNodeArray;
}

function validateFormState(
  formState: SetupFormState,
  appState: AppState,
  t: TFunction,
  options?: { showStoreCountry?: boolean }
): SetupFormErrors {
  const i18nOptions = { lng: formState.language || appState.language };
  const formErrors: SetupFormErrors = {
    response: [],
    language: [],
    zone: [],
    storeId: [],
    storeCountry: []
  };

  if (!formState.language) {
    formErrors.language.push(
      t("setup.fields.language.errors.required", i18nOptions)
    );
  }

  if (!formState.zone) {
    formErrors.zone.push(t("setup.fields.zone.errors.required", i18nOptions));
  }

  if (!formState.storeId) {
    formErrors.storeId.push(
      t("setup.fields.store.errors.required", i18nOptions)
    );
  }

  if (get(options, "showStoreCountry") && !formState.storeCountry) {
    formErrors.storeCountry.push(
      t("setup.fields.storeCountry.errors.required", i18nOptions)
    );
  }

  return formErrors;
}

export function SetupPage() {
  // Shared context/state
  const { t, superT, i18n } = useT();
  const [appState, doAppStateAction] = useContext(AppStateContext);
  const config = appState.config;

  // Internal state
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState<SetupFormState>({
    language: appState.language,
    zone: appState.zone || appState.config.defaultZone,
    storeId: "", //appState.storeId,
    storeCountry: appState.storeCountry
  });
  const [formErrors, setFormErrors] = useState<SetupFormErrors>({
    response: [],
    language: [],
    zone: [],
    storeId: [],
    storeCountry: []
  });
  const stores = useAsyncRetry(async () => await loadStores(config.stores), []);
  const [loadingStoreCountry, setLoadingStoreCountry] = useState(false);
  const [showStoreCountry, setShowStoreCountry] = useState(false);

  const isFormCompleted =
    !!formState.language &&
    !!formState.zone &&
    !!formState.storeId &&
    !!formState.storeCountry;

  // Event handlers
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.persist();
    event.preventDefault();

    setIsLoading(true);

    // Validate the formState
    const updateFormErrors = validateFormState(formState, appState, superT);
    const hasFormErrors = Object.values(updateFormErrors).reduce(
      (acc, fieldErrors) => acc + fieldErrors.length,
      0
    );

    // Errors found, so prevent submission
    if (hasFormErrors > 0) {
      setFormErrors(updateFormErrors);
      setIsLoading(false);
      return;
    }

    // Save the form's state to the app state
    doAppStateAction(actions.setup(formState));
    doAppStateAction(actions.setPage(APP_PAGE.ACTIONS));
  };

  // - This is a custom input event handler, which assumes custom event handlers will use the same function signature
  const handleFieldChange = (newValue: any, fieldName: string): void => {
    let updateFormState = set({ ...formState }, fieldName, newValue);
    if (fieldName === "zone" && config.customise.customer.enableCompleteSetup) {
      updateFormState = set({ ...updateFormState }, "storeId", "");
    }

    const updateFormErrors = validateFormState(updateFormState, appState, t);

    // Commit the change to the form
    setFormState(updateFormState);
    setFormErrors(updateFormErrors);

    // Make sure to get the storeCountry when the storeId is changed
    if (fieldName === "storeId") {
      setLoadingStoreCountry(true);
      getStoreById(newValue)
        .then(store => {
          setLoadingStoreCountry(false);
          setFormState({
            ...updateFormState,
            storeCountry: get(store, "geo.addressCountry")
          });
        })
        .catch(err => {
          debug.error(err);
          setLoadingStoreCountry(false);
          setShowStoreCountry(true);
          setFormErrors({
            ...updateFormErrors,
            storeCountry: [
              t("setup.fields.storeCountry.errors.dataUnavailable")
            ]
          });
        });
    }
  };

  const handleFieldSelectChangeFn = (
    fieldName: string
  ): ReactSelectChangeEventHandler => (newValue, { action }): void => {
    switch (action) {
      default:
        handleFieldChange(newValue, fieldName);
        break;

      case "clear":
        handleFieldChange(null, fieldName);
        break;
    }
  };

  // Memoized values
  const i18nOptions = useMemo(() => ({ lng: formState.language }), [
    formState.language
  ]);

  const languageSelectOptions: ReactSelectOptionObject[] = useMemo(
    () =>
      (
        (config.zones[formState.zone] &&
          config.zones[formState.zone].supportedLanguages) ||
        config.languages.supportedLanguages
      ).map((lang: string) => {
        return {
          label: getNameByLocale(lang) || lang,
          value: lang,
          dir: isFunction(i18n.dir) ? i18n.dir(lang) : undefined
        };
      }),
    [config.languages.supportedLanguages, formState.zone]
  );

  const zoneSelectOptions: ReactSelectOptionObject[] = useMemo(
    () =>
      Object.keys(config.zones)
        .filter((zoneKey: string) => !config.zones[zoneKey].disabled)
        .map((zoneKey: string) => ({
          label: t(`zones.${zoneKey}`, {
            ...i18nOptions,
            defaultValue: config.zones[zoneKey].label || zoneKey
          }),
          value: zoneKey
        })),
    [t, formState.language, formState.zone, config.zones]
  );

  const storeIdSelectOptions: any[] = useMemo(() => {
    if (!stores.loading && !stores.error && !!stores.value) {
      return stores.value
        .filter(
          (store: LocalBusiness) =>
            !config.customise.setupPage.filterStoresByZone ||
            !formState.zone ||
            (store.geo &&
              formState.zone.toLowerCase() ===
                store.geo.addressCountry.toLowerCase())
        )
        .map(store => {
          return {
            label: store.name,
            value: store.identifier
          };
        });
    } else {
      return [];
    }
  }, [stores.loading, stores.error, stores.value, formState.zone]);
  // set again storeId to display the value in the field
  formState.storeId = formState.storeId + "";

  // Output async storeId error message
  const formErrorsStoreId: React.ReactNodeArray = formErrors.storeId.slice(0);
  if (stores.error) {
    formErrorsStoreId.push(
      <>
        {superT("setup.fields.store.errors.dataUnavailable", {
          ...i18nOptions,
          errorMessage: stores.error.message
        })}
        <Button
          label={t("general.buttons.retry.label", i18nOptions)}
          shape="link"
          onClick={(event: React.MouseEvent) => {
            event.preventDefault();
            stores.retry();
          }}
        />
      </>
    );
  }

  // Make sure to load the language, if it hasn't been loaded already
  useEffect(() => {
    if (i18n.language && formState.language !== i18n.language) {
      i18n.changeLanguage(formState.language);
    }
  }, [i18n.language, formState.language]);

  if (
    !formState.storeId &&
    stores &&
    stores.value &&
    stores.value.length === 1
  ) {
    handleFieldChange(stores.value[0].identifier, "storeId");
  }

  return (
    <AppFrame
      headerLeftContent={
        checkAppCorrectlySetup(appState) ? (
          <AppLayoutHeaderLeftCancel lang={formState.language} />
        ) : null
      }
    >
      <Page
        name="setup"
        title={t("setup.page.title", i18nOptions)}
        lang={formState.language}
      >
        <form className="setup-form" onSubmit={handleFormSubmit}>
          <Field
            type="select-custom"
            id="appLanguage"
            name="language"
            label={t("setup.fields.language.label", i18nOptions)}
            icon="language"
            value={formState.language}
            errors={formErrors.language}
            required
            onChange={handleFieldSelectChangeFn("language")}
            inputProps={{
              sortOptionsAlphabetically: false,
              options: languageSelectOptions,
              searchable: true,
              clearable: false
            }}
          />
          <Field
            type="select-custom"
            id="appZone"
            name="zone"
            label={t("setup.fields.zone.label", i18nOptions)}
            icon="location"
            value={formState.zone}
            errors={formErrors.zone}
            required
            onChange={handleFieldSelectChangeFn("zone")}
            inputProps={{
              sortOptionsAlphabetically: false,
              options: zoneSelectOptions,
              searchable: true,
              clearable: false
            }}
          />
          <Field
            type="select-custom"
            id="appStore"
            name="storeId"
            label={t("setup.fields.store.label", i18nOptions)}
            icon={stores.loading ? "loading" : "store"}
            value={formState.storeId}
            errors={formErrorsStoreId}
            required
            disabled={!!stores.loading || !!stores.error}
            onChange={handleFieldSelectChangeFn("storeId")}
            inputProps={{
              options: storeIdSelectOptions,
              searchable: true,
              clearable: false
            }}
          />
          {showStoreCountry && (
            <Field
              type="country"
              id="appStoreCountry"
              name="storeCountry"
              label={t("setup.fields.storeCountry.label", i18nOptions)}
              icon={loadingStoreCountry ? "loading" : "location"}
              value={formState.storeCountry}
              errors={formErrors.storeCountry}
              required
              disabled={
                !!loadingStoreCountry || !!stores.loading || !!stores.error
              }
              onChange={handleFieldSelectChangeFn("storeCountry")}
              inputProps={{
                clearable: false
              }}
            />
          )}
          <div className="button-group">
            <Button
              type="submit"
              icon={isLoading ? "loading" : undefined}
              label={t("setup.buttons.submit.label", i18nOptions)}
              theme="primary"
              shape="solid"
              fullWidth
              disabled={!isFormCompleted || isLoading || loadingStoreCountry}
            />
          </div>
        </form>
      </Page>
    </AppFrame>
  );
}

export default SetupPage;
